import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Pencil, Trash2, Plus, ExternalLink, X, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const emptyAgent = () => ({
  name: '',
  title: 'REALTOR®',
  slug: '',
  email: '',
  phone: '',
  photo: '',
  bio: '',
  tagline: '',
  specialties: '',
  service_areas: '',
  licenses: { tn: '', ms: '', ar: '' },
  mls_agent_ids: { maar: '', cmmls: '' },
  active: true,
});

const AgentSitesAdmin = () => {
  const [sites, setSites] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | site object
  const [form, setForm] = useState(emptyAgent());
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSites(); }, []);

  const loadSites = async () => {
    try {
      const { data } = await api.get('/agent-sites');
      setSites(data);
    } catch (e) {
      toast.error('Failed to load agent websites');
    }
  };

  const startNew = () => { setForm(emptyAgent()); setEditing('new'); };

  const startEdit = (site) => {
    setForm({
      ...site,
      specialties: (site.specialties || []).join(', '),
      service_areas: (site.service_areas || []).join(', '),
      licenses: site.licenses || { tn: '', ms: '', ar: '' },
      mls_agent_ids: site.mls_agent_ids || { maar: '', cmmls: '' },
    });
    setEditing(site);
  };

  const cancel = () => { setEditing(null); setForm(emptyAgent()); };

  const save = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error('Name, email, and phone are required');
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      title: form.title || 'REALTOR®',
      slug: form.slug || undefined,
      email: form.email,
      phone: form.phone,
      photo: form.photo || null,
      bio: form.bio || '',
      tagline: form.tagline || '',
      specialties: (form.specialties || '').split(',').map((s) => s.trim()).filter(Boolean),
      service_areas: (form.service_areas || '').split(',').map((s) => s.trim()).filter(Boolean),
      licenses: {
        tn: form.licenses?.tn || null,
        ms: form.licenses?.ms || null,
        ar: form.licenses?.ar || null,
      },
      mls_agent_ids: {
        maar: form.mls_agent_ids?.maar || null,
        cmmls: form.mls_agent_ids?.cmmls || null,
      },
      active: !!form.active,
    };
    try {
      if (editing === 'new') {
        await api.post('/agent-sites', payload);
        toast.success('Agent website created');
      } else {
        await api.patch(`/agent-sites/id/${editing.id}`, payload);
        toast.success('Agent website updated');
      }
      cancel();
      loadSites();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Save failed';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (site) => {
    if (!window.confirm(`Delete ${site.name}'s website? This cannot be undone.`)) return;
    try {
      await api.delete(`/agent-sites/id/${site.id}`);
      toast.success(`${site.name}'s website deleted`);
      loadSites();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setNested = (obj, key, value) => setForm((f) => ({ ...f, [obj]: { ...(f[obj] || {}), [key]: value } }));

  if (editing) {
    return (
      <Card data-testid="agent-site-form-card">
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>{editing === 'new' ? 'New Agent Website' : `Edit: ${editing.name}`}</CardTitle>
            <CardDescription>Public page at <code>bespokerealty.net/agents/{form.slug || 'slug-will-be-generated'}</code></CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={cancel} data-testid="agent-form-cancel"><X className="h-4 w-4" /></Button>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Full Name *</Label>
              <Input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Kenya Jones" data-testid="agent-form-name" />
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setField('title', e.target.value)} placeholder="REALTOR®, Licensed in MS & TN" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="kenya@bespokerealty.net" data-testid="agent-form-email" />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="(901) 238-7628" data-testid="agent-form-phone" />
            </div>
          </div>

          <div>
            <Label>Slug (URL) — auto-generated if blank</Label>
            <Input value={form.slug} onChange={(e) => setField('slug', e.target.value)} placeholder="kenya-jones" data-testid="agent-form-slug" />
          </div>

          <div>
            <Label>Photo URL</Label>
            <Input value={form.photo} onChange={(e) => setField('photo', e.target.value)} placeholder="https://..." />
          </div>

          <div>
            <Label>Tagline (one line, appears under name)</Label>
            <Input value={form.tagline} onChange={(e) => setField('tagline', e.target.value)} placeholder="Your trusted guide across Memphis and the Mid-South" />
          </div>

          <div>
            <Label>Bio</Label>
            <Textarea rows={5} value={form.bio} onChange={(e) => setField('bio', e.target.value)} placeholder="A short professional bio..." />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Specialties (comma-separated)</Label>
              <Input value={form.specialties} onChange={(e) => setField('specialties', e.target.value)} placeholder="First-Time Buyers, Relocation, Investment" />
            </div>
            <div>
              <Label>Service Areas (comma-separated)</Label>
              <Input value={form.service_areas} onChange={(e) => setField('service_areas', e.target.value)} placeholder="Memphis, Germantown, Southaven" />
            </div>
          </div>

          <div className="border rounded-lg p-4 bg-[#faf8f4]">
            <p className="text-sm font-semibold mb-3">State License Numbers</p>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Tennessee</Label>
                <Input value={form.licenses.tn} onChange={(e) => setNested('licenses', 'tn', e.target.value)} placeholder="375143" />
              </div>
              <div>
                <Label className="text-xs">Mississippi</Label>
                <Input value={form.licenses.ms} onChange={(e) => setNested('licenses', 'ms', e.target.value)} placeholder="57385" />
              </div>
              <div>
                <Label className="text-xs">Arkansas</Label>
                <Input value={form.licenses.ar} onChange={(e) => setNested('licenses', 'ar', e.target.value)} placeholder="" />
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-4 bg-[#faf8f4]">
            <p className="text-sm font-semibold mb-1">MLS Agent IDs (for Featured Listings)</p>
            <p className="text-xs text-gray-500 mb-3">These are short 4-5 digit MLS IDs (different from state license numbers). Leave blank if agent doesn&apos;t have listings in that MLS.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">MAAR (Tennessee)</Label>
                <Input value={form.mls_agent_ids.maar} onChange={(e) => setNested('mls_agent_ids', 'maar', e.target.value)} placeholder="e.g. 12345" />
              </div>
              <div>
                <Label className="text-xs">CMMLS (Mississippi)</Label>
                <Input value={form.mls_agent_ids.cmmls} onChange={(e) => setNested('mls_agent_ids', 'cmmls', e.target.value)} placeholder="e.g. 3077" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Switch checked={form.active} onCheckedChange={(v) => setField('active', v)} data-testid="agent-form-active" />
            <Label>Active (public page visible)</Label>
          </div>

          <div className="flex gap-3 pt-3 border-t">
            <Button onClick={save} disabled={saving} data-testid="agent-form-save" className="bg-[#BCA480] hover:bg-[#a89470]">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : editing === 'new' ? 'Create Website' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={cancel}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="agent-sites-list">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold">Agent Websites</h3>
          <p className="text-sm text-gray-500">Create branded landing pages for each licensed agent</p>
        </div>
        <Button onClick={startNew} data-testid="agent-add-new" className="bg-[#BCA480] hover:bg-[#a89470]">
          <Plus className="h-4 w-4 mr-2" /> Add Agent Website
        </Button>
      </div>

      {sites.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-gray-500">
            <p className="mb-4">No agent websites yet.</p>
            <Button onClick={startNew} className="bg-[#BCA480] hover:bg-[#a89470]"><Plus className="h-4 w-4 mr-2" /> Add Your First Agent</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {sites.map((s) => (
            <Card key={s.id} data-testid={`agent-site-row-${s.slug}`}>
              <CardContent className="p-4 flex items-center gap-4">
                {s.photo ? (
                  <img src={s.photo} alt={s.name} className="w-14 h-14 rounded-full object-cover border-2 border-[#BCA480]" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#BCA480] text-white flex items-center justify-center font-bold">
                    {s.name.split(' ').map((n) => n[0]).slice(0, 2).join('')}
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-semibold">{s.name} <span className={`ml-2 text-xs px-2 py-0.5 rounded ${s.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{s.active ? 'Active' : 'Inactive'}</span></p>
                  <p className="text-sm text-gray-500">{s.title}</p>
                  <a href={`/agents/${s.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#BCA480] hover:underline inline-flex items-center gap-1 mt-1">
                    /agents/{s.slug} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <Button variant="outline" size="sm" onClick={() => startEdit(s)} data-testid={`agent-edit-${s.slug}`}>
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => remove(s)} className="text-red-600 hover:text-red-700" data-testid={`agent-delete-${s.slug}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentSitesAdmin;
